import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Settings2, Database, Plus, Search, Pencil, Trash2, Info, FileText, Users, HardDrive, Server, Table2, Activity, Calendar, Zap } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  useConfiguraciones,
  useConfiguracion,
  useToggleActivo,
  useDeleteConfiguracion,
} from '@/modules/registros/hooks/useConfiguraciones'
import { ConfiguracionFormSheet } from '@/modules/registros/components/ConfiguracionFormSheet'
import { MarcadoresDialog } from '@/modules/registros/components/MarcadoresDialog'
import { useAuth } from '@/auth/context/AuthContext'
import { useTeam } from '@/shared/context/TeamContext'

export function ConfiguracionPage() {
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()

  // Check if user is admin
  const isTonalAdmin = user?.groups?.includes('admin') || user?.is_superuser

  // Redirect non-admin users
  useEffect(() => {
    if (!authLoading && !isTonalAdmin) {
      navigate('/registros/reportes', { replace: true })
    }
  }, [authLoading, isTonalAdmin, navigate])
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [showInfo, setShowInfo] = useState(false)
  const [showFilterInfo, setShowFilterInfo] = useState(false)
  const [marcadoresOpen, setMarcadoresOpen] = useState(false)
  const [marcadoresConfig, setMarcadoresConfig] = useState<{ id: number; titulo: string } | null>(null)

  // Filtrar por área seleccionada
  const { selectedArea, selectedTeam } = useTeam()
  const isClientArea = selectedTeam !== 'Admin' && selectedTeam !== 'Tonal'
  const areaGrupoId = isClientArea ? selectedArea?.laboratorio_id : undefined

  const { data, isLoading, error } = useConfiguraciones({
    page: 1,
    page_size: 100,
    search: search || undefined,
    activo: showInactive ? undefined : true,
    grupo_id: areaGrupoId ?? undefined,
  })

  const { data: editingConfig } = useConfiguracion(editingId || 0)

  const toggleActivoMutation = useToggleActivo()
  const deleteConfigMutation = useDeleteConfiguracion()

  const handleToggleActivo = async (id: number) => {
    try {
      await toggleActivoMutation.mutateAsync(id)
    } catch (error) {
      console.error('Error al cambiar estado:', error)
    }
  }

  const handleDelete = async (id: number, titulo: string) => {
    if (window.confirm(`¿Estás seguro de eliminar la configuración "${titulo}"?`)) {
      try {
        await deleteConfigMutation.mutateAsync(id)
      } catch (error) {
        console.error('Error al eliminar:', error)
      }
    }
  }

  const handleCreate = () => {
    setEditingId(null)
    setFormOpen(true)
  }

  const handleEdit = (id: number) => {
    setEditingId(id)
    setFormOpen(true)
  }

  const handleFormClose = (open: boolean) => {
    setFormOpen(open)
    if (!open) {
      setEditingId(null)
    }
  }

  const handleOpenMarcadores = (id: number, titulo: string) => {
    setMarcadoresConfig({ id, titulo })
    setMarcadoresOpen(true)
  }

  const handleCloseMarcadores = (open: boolean) => {
    setMarcadoresOpen(open)
    if (!open) {
      setMarcadoresConfig(null)
    }
  }

  // Don't render content while checking auth or if user is not admin
  if (authLoading || !isTonalAdmin) {
    return null
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
            <Settings2 className="mx-auto h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold text-destructive mb-2">
              Error al cargar configuraciones
            </h3>
            <p className="text-sm text-muted-foreground">
              {(error as any)?.response?.data?.error ||
                'No se pudieron cargar las configuraciones. Por favor, intenta nuevamente.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Settings2 className="h-6 w-6" />
              Configuración de Registros
            </h1>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="text-muted-foreground hover:text-foreground transition-colors ml-2"
            >
              <Info className="h-5 w-5" />
            </button>
            {showInfo && (
              <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                Gestiona las configuraciones de conexión a bases de datos externas
              </span>
            )}
          </div>
          <Button onClick={handleCreate} className="bg-teal-800 hover:bg-teal-900 text-white">
            <Plus className="mr-2 h-4 w-4" />
            Nueva Configuración
          </Button>
        </div>

        {/* Filters */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <div className="flex items-center">
              <CardTitle className="text-lg">Filtros</CardTitle>
              <button
                onClick={() => setShowFilterInfo(!showFilterInfo)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showFilterInfo && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Busca y filtra las configuraciones disponibles
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por título, grupo, o base de datos..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant={showInactive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowInactive(!showInactive)}
                className={showInactive ? 'bg-teal-800 hover:bg-teal-900 text-white' : ''}
              >
                {showInactive ? 'Mostrar solo activos' : 'Mostrar todos'}
              </Button>
            </div>

            {data && (
              <Badge className="badge-gradient text-xs inline-flex items-center gap-1.5 w-fit">
                <Database className="h-3 w-3" />
                {data.count} {data.count === 1 ? 'configuración' : 'configuraciones'}
              </Badge>
            )}
          </CardContent>
        </Card>

        {/* Configurations Table */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              Configuraciones
              {data && (
                <Badge className="bg-transparent border-[0.5px] border-teal-500 text-teal-700 dark:text-teal-400 text-xs px-2 py-0 h-5 hover:bg-transparent shadow-none">
                  {data.count}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : data && data.results.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                    <TableRow>
                      <TableHead><div className="flex items-center gap-2"><FileText className="h-4 w-4" />Título</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Users className="h-4 w-4" />Grupo</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><HardDrive className="h-4 w-4" />Motor DB</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Server className="h-4 w-4" />Host</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Table2 className="h-4 w-4" />Tabla</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Activity className="h-4 w-4" />Estado</div></TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.results.map((config) => (
                      <TableRow key={config.id}>
                        <TableCell className="font-medium">{config.titulo_reporte}</TableCell>
                        <TableCell>{config.grupo_nombre}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{config.db_engine.toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{config.db_host}</TableCell>
                        <TableCell className="font-mono text-xs">{config.tabla_nombre}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={config.activo}
                              onCheckedChange={() => handleToggleActivo(config.id)}
                              disabled={toggleActivoMutation.isPending}
                              className={config.activo ? 'data-[state=checked]:bg-teal-500' : 'data-[state=unchecked]:bg-gray-300'}
                            />
                            <Badge className={config.activo ? 'badge-gradient shadow-none' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 shadow-none'}>
                              {config.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {config.asistencia_presencial && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/registros/asistencias/${config.id}`)}
                                title="Ver Asistencias"
                              >
                                <Zap className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenMarcadores(config.id, config.titulo_reporte)}
                              title="Gestionar Hitos"
                            >
                              <Calendar className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(config.id)}
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(config.id, config.titulo_reporte)}
                              disabled={deleteConfigMutation.isPending}
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4 text-gray-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Settings2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No se encontraron configuraciones</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {search
                    ? 'No hay configuraciones que coincidan con tu búsqueda.'
                    : 'No hay configuraciones creadas aún.'}
                </p>
                <Button onClick={handleCreate} className="bg-teal-800 hover:bg-teal-900 text-white">
                  <Plus className="mr-2 h-4 w-4" />
                  Crear Primera Configuración
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

      {/* Form Sheet */}
      <ConfiguracionFormSheet
        open={formOpen}
        onOpenChange={handleFormClose}
        configuracion={editingId ? editingConfig : null}
      />

      {/* Marcadores Dialog */}
      {marcadoresConfig && (
        <MarcadoresDialog
          open={marcadoresOpen}
          onOpenChange={handleCloseMarcadores}
          configId={marcadoresConfig.id}
          configTitulo={marcadoresConfig.titulo}
        />
      )}
    </div>
  )
}
